/**
 * Maps backend error codes to i18n keys so user-facing copy is translated
 * instead of showing raw backend strings.
 *
 * Two flavors of code live here, distinguished by their prefix:
 *   * **Envelope codes** ``APP.TYPE.ID`` (e.g. ``TEAM.VAL.035``) — emitted in
 *     the response envelope's ``error_code`` field for whole-request errors.
 *     Defined per-AppException subclass on the backend. New entries should
 *     follow the **best-practice contract**: backend emits a stable flat
 *     ``details`` dict (e.g. ``{ field_label, entity_type, ... }``) which the
 *     ErrorHandler passes through to ``i18n.t()`` as interpolation params, so
 *     the localized string can use ``{{field_label}}`` etc. See
 *     ``approval_request.exceptions.RelatedEntityNotFound`` (APRQ.VAL.121) as
 *     the canonical template.
 *   * **Field-level codes** ``field.<kind>`` (e.g. ``field.unique``) —
 *     emitted inside ``details`` by serializers raising
 *     ``StructuredValidationError`` (see ``approveit/exceptions.py``). The FE
 *     renderer interpolates ``params`` (e.g. ``{resource, field}``) into the
 *     matched i18n string. Future kinds: ``field.required``,
 *     ``field.max_length``, ``field.format``, etc.
 *
 * If a code has no entry here, the renderer falls back to the server's
 * ``message`` field.
 */
export const ERROR_CODE_I18N_MAP: Record<string, string> = {
  'TEAM.VAL.035': 'common:invites.alreadyMember',
  'ACC.VAL.112': 'request:form.unboundVendorError',
  'ACC.VAL.113': 'accounting:org.errors.integrationNotLinked',
  'APRQ.BL.106': 'common:errors.staleRequestState',
  'APRQ.BL.117': 'common:errors.noUserInJobRole',
  'APRQ.VAL.121': 'common:errors.relationNotFound',
  'APRQ.VAL.122': 'common:errors.relationIdInvalid',
  'AUTH.SEC.001': 'common:errors.loginLocked',
  // Custom lists. TEAM.VAL.044 (import rejected) is deliberately absent —
  // it carries per-row errors that the sidebar renders as a list, so a
  // single toast line would lose the detail the admin needs.
  'TEAM.VAL.039': 'team:customLists.errors.nameAlreadyExists',
  'TEAM.VAL.045': 'team:customLists.errors.labelAlreadyExists',
  'TEAM.BL.042': 'team:customLists.errors.itemInUse',
  // The delete-blocked 409. The sidebar renders a richer popup naming the
  // workflows, but that only fires where the popup exists — every other
  // caller was falling through to a generic failure with no reason given.
  'TEAM.BL.041': 'team:customLists.errors.listInUseGeneric',
  // The per-list ownership 403. Without this, being refused an edit on a
  // list another role owns showed as an unexplained error.
  'TEAM.BL.046': 'team:customLists.errors.notEditableByRole',
  'field.unique': 'common:errors.uniqueField',
}
